import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  MapPin, 
  Plus, 
  Search,
  MoreVertical,
  Pencil,
  Trash2,
  Building2,
  Download,
  ChevronLeft,
  ChevronRight,
  Eye,
  Briefcase,
  Filter,
  Package,
  Users,
  CheckCircle,
  Clock,
  AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  inactive: 'bg-gray-50 text-gray-700 border-gray-200',
  completed: 'bg-blue-50 text-blue-700 border-blue-200',
  not_started: 'bg-gray-100 text-gray-600',
  in_progress: 'bg-amber-100 text-amber-700',
  pending: 'bg-amber-100 text-amber-700',
  ordered: 'bg-blue-100 text-blue-700',
  received: 'bg-green-100 text-green-700',
  issued: 'bg-blue-100 text-blue-700',
  handed_over: 'bg-teal-100 text-teal-700',
  signed: 'bg-emerald-100 text-emerald-700',
  none: 'bg-gray-100 text-gray-600',
  open: 'bg-red-100 text-red-700',
  resolved: 'bg-green-100 text-green-700'
};

const Sites = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  const { currencySymbol } = useSettings();
  
  const [sites, setSites] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [formData, setFormData] = useState({});
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterProject, setFilterProject] = useState('all');
  const [filterPOStatus, setFilterPOStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const statusLabels = {
    active: isRTL ? 'نشط' : 'Active',
    inactive: isRTL ? 'غير نشط' : 'Inactive',
    completed: isRTL ? 'مكتمل' : 'Completed',
    not_started: isRTL ? 'لم يبدأ' : 'Not Started',
    in_progress: isRTL ? 'جاري' : 'In Progress',
    pending: isRTL ? 'قيد الانتظار' : 'Pending',
    ordered: isRTL ? 'تم الطلب' : 'Ordered',
    received: isRTL ? 'تم الاستلام' : 'Received',
    issued: isRTL ? 'تم الإصدار' : 'Issued',
    handed_over: isRTL ? 'تم التسليم' : 'Handed Over',
    signed: isRTL ? 'موقع' : 'Signed',
    none: isRTL ? 'لا يوجد' : 'None',
    open: isRTL ? 'مفتوح' : 'Open',
    resolved: isRTL ? 'تم الحل' : 'Resolved'
  };

  useEffect(() => {
    fetchSites();
    fetchClients();
    fetchProjects();
  }, []);

  const fetchSites = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/jobs/sites`);
      setSites(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/crm/clients`);
      setClients(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/jobs/projects/list`);
      setProjects(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        location: {
          address: formData.address || '',
          city: formData.city || '',
          country: formData.country || 'Saudi Arabia'
        }
      };
      
      if (editingSite) {
        await axios.put(`${API_URL}/api/jobs/sites/${editingSite.id}`, payload);
        toast.success(t('jobManagement.siteUpdated'));
      } else {
        await axios.post(`${API_URL}/api/jobs/sites`, payload);
        toast.success(t('jobManagement.siteCreated'));
      }
      setShowDialog(false);
      setEditingSite(null);
      setFormData({});
      fetchSites();
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' 
        ? errorDetail 
        : (Array.isArray(errorDetail) ? errorDetail.map(e => e.msg || e).join(', ') : t('jobManagement.failedToSave'));
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('jobManagement.confirmDeleteSite'))) {
      try {
        await axios.delete(`${API_URL}/api/jobs/sites/${id}`);
        toast.success(t('jobManagement.siteDeleted'));
        fetchSites();
      } catch (err) {
        toast.error(err.response?.data?.detail || t('jobManagement.failedToDelete'));
      }
    }
  };

  const openNew = () => {
    setEditingSite(null);
    setFormData({});
    setShowDialog(true);
  };

  const openEdit = (site) => {
    setEditingSite(site);
    setFormData({
      ...site,
      address: site.location?.address || '',
      city: site.location?.city || ''
    });
    setShowDialog(true);
  };

  // Filter and search sites
  const filteredSites = useMemo(() => {
    return sites.filter(s => {
      // Search filter - search by Site ID, PO number, name, client
      const searchLower = search.toLowerCase();
      const matchesSearch = !search || 
        s.site_number?.toLowerCase().includes(searchLower) ||
        s.name?.toLowerCase().includes(searchLower) ||
        s.client_name?.toLowerCase().includes(searchLower) ||
        s.project_name?.toLowerCase().includes(searchLower) ||
        s.workflow?.purchase_order?.po_number?.toLowerCase().includes(searchLower);
      
      // Status filter
      const matchesStatus = filterStatus === 'all' || s.status === filterStatus;
      
      // Project filter
      const matchesProject = filterProject === 'all' || s.project_id === filterProject;
      
      // PO Status filter
      const poStatus = s.workflow?.purchase_order?.status || 'not_started';
      const matchesPOStatus = filterPOStatus === 'all' || poStatus === filterPOStatus;
      
      return matchesSearch && matchesStatus && matchesProject && matchesPOStatus;
    });
  }, [sites, search, filterStatus, filterProject, filterPOStatus]);

  // Pagination
  const totalPages = Math.ceil(filteredSites.length / itemsPerPage);
  const paginatedSites = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredSites.slice(start, start + itemsPerPage);
  }, [filteredSites, currentPage]);

  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      'Site ID', 'Site Name', 'Project', 'Client', 'City',
      'PO Number', 'PO Status', 'PO Amount',
      'Material Issue', 'Material Receive', 
      'Team Assigned', 'Implementation',
      'Site Complete', 'Handover', 'PAT', 
      'Troubleshoot', 'Invoice Status',
      'Progress %', 'Status'
    ];
    
    const rows = filteredSites.map(site => {
      const wf = site.workflow || {};
      return [
        site.site_number,
        site.name,
        site.project_name || '',
        site.client_name || '',
        site.location?.city || '',
        wf.purchase_order?.po_number || '',
        wf.purchase_order?.status || 'not_started',
        wf.purchase_order?.amount || 0,
        wf.material_issue?.status || 'not_started',
        wf.material_receive?.status || 'not_started',
        (wf.team_assigned?.length || 0) + ' members',
        wf.implementation?.status || 'not_started',
        wf.site_complete?.status || 'not_started',
        wf.handover?.status || 'not_started',
        wf.pat_document?.status || 'not_started',
        wf.troubleshoot?.status || 'none',
        (wf.invoices?.length || 0) + ' invoices',
        (wf.overall_progress || 0) + '%',
        site.status
      ];
    });
    
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `sites_export_${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
    toast.success(isRTL ? 'تم تصدير البيانات' : 'Sites exported to CSV');
  };

  // Stats
  const stats = useMemo(() => {
    const total = sites.length;
    const active = sites.filter(s => s.status === 'active').length;
    const completed = sites.filter(s => s.status === 'completed').length;
    const withPO = sites.filter(s => s.workflow?.purchase_order?.po_number).length;
    const inProgress = sites.filter(s => (s.workflow?.overall_progress || 0) > 0 && (s.workflow?.overall_progress || 0) < 100).length;
    return { total, active, completed, withPO, inProgress };
  }, [sites]);

  const getWorkflowBadge = (status) => {
    if (!status || status === 'not_started') {
      return <Badge variant="outline" className="text-xs bg-gray-50 text-gray-500">-</Badge>;
    }
    return <Badge className={`text-xs ${statusColors[status] || statusColors.not_started}`}>{statusLabels[status] || status}</Badge>;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className={`space-y-6 ${isRTL ? 'rtl' : ''}`}>
      {/* Header */}
      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MapPin className="h-6 w-6" />
            {t('jobManagement.sites')}
          </h1>
          <p className="text-muted-foreground">{isRTL ? 'إدارة جميع مواقع المشاريع' : 'Manage all project sites'}</p>
        </div>
        <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <Button variant="outline" onClick={exportToCSV}>
            <Download className="h-4 w-4 mr-2" />
            {isRTL ? 'تصدير CSV' : 'Export CSV'}
          </Button>
          <Button onClick={openNew}>
            <Plus className="h-4 w-4 mr-2" />
            {t('jobManagement.newSite')}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-sm text-muted-foreground">{isRTL ? 'إجمالي المواقع' : 'Total Sites'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-emerald-600">{stats.active}</div>
            <p className="text-sm text-muted-foreground">{isRTL ? 'نشط' : 'Active'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-blue-600">{stats.withPO}</div>
            <p className="text-sm text-muted-foreground">{isRTL ? 'لديه أمر شراء' : 'With PO'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-amber-600">{stats.inProgress}</div>
            <p className="text-sm text-muted-foreground">{isRTL ? 'قيد التنفيذ' : 'In Progress'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
            <p className="text-sm text-muted-foreground">{isRTL ? 'مكتمل' : 'Completed'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className={`flex flex-wrap items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {/* Search */}
            <div className="relative flex-1 min-w-[300px]">
              <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground`} />
              <Input 
                className={isRTL ? 'pr-10' : 'pl-10'} 
                placeholder={isRTL ? 'بحث برقم الموقع، أمر الشراء، الاسم...' : 'Search by Site ID, PO Number, Name...'} 
                value={search} 
                onChange={e => { setSearch(e.target.value); setCurrentPage(1); }} 
              />
            </div>
            
            {/* Project Filter */}
            <Select value={filterProject} onValueChange={v => { setFilterProject(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-48">
                <Briefcase className="h-4 w-4 mr-2" />
                <SelectValue placeholder={isRTL ? 'المشروع' : 'Project'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{isRTL ? 'كل المشاريع' : 'All Projects'}</SelectItem>
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.project_name || p.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* PO Status Filter */}
            <Select value={filterPOStatus} onValueChange={v => { setFilterPOStatus(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-40">
                <Package className="h-4 w-4 mr-2" />
                <SelectValue placeholder={isRTL ? 'حالة PO' : 'PO Status'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{isRTL ? 'الكل' : 'All'}</SelectItem>
                <SelectItem value="not_started">{isRTL ? 'لم يبدأ' : 'Not Started'}</SelectItem>
                <SelectItem value="pending">{isRTL ? 'قيد الانتظار' : 'Pending'}</SelectItem>
                <SelectItem value="ordered">{isRTL ? 'تم الطلب' : 'Ordered'}</SelectItem>
                <SelectItem value="received">{isRTL ? 'تم الاستلام' : 'Received'}</SelectItem>
              </SelectContent>
            </Select>

            {/* Status Filter */}
            <Select value={filterStatus} onValueChange={v => { setFilterStatus(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-36">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder={isRTL ? 'الحالة' : 'Status'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{isRTL ? 'الكل' : 'All'}</SelectItem>
                <SelectItem value="active">{isRTL ? 'نشط' : 'Active'}</SelectItem>
                <SelectItem value="inactive">{isRTL ? 'غير نشط' : 'Inactive'}</SelectItem>
                <SelectItem value="completed">{isRTL ? 'مكتمل' : 'Completed'}</SelectItem>
              </SelectContent>
            </Select>

            <span className="text-sm text-muted-foreground">
              {filteredSites.length} {isRTL ? 'موقع' : 'sites'}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Excel-like Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="font-semibold whitespace-nowrap">{isRTL ? 'رقم الموقع' : 'Site ID'}</TableHead>
                  <TableHead className="font-semibold whitespace-nowrap">{isRTL ? 'اسم الموقع' : 'Site Name'}</TableHead>
                  <TableHead className="font-semibold whitespace-nowrap">{isRTL ? 'المشروع' : 'Project'}</TableHead>
                  <TableHead className="font-semibold whitespace-nowrap">{isRTL ? 'العميل' : 'Client'}</TableHead>
                  <TableHead className="font-semibold whitespace-nowrap">{isRTL ? 'رقم PO' : 'PO #'}</TableHead>
                  <TableHead className="font-semibold whitespace-nowrap">{isRTL ? 'حالة PO' : 'PO Status'}</TableHead>
                  <TableHead className="font-semibold whitespace-nowrap text-right">{isRTL ? 'مبلغ PO' : 'PO Amount'}</TableHead>
                  <TableHead className="font-semibold whitespace-nowrap">{isRTL ? 'المواد' : 'Material'}</TableHead>
                  <TableHead className="font-semibold whitespace-nowrap">{isRTL ? 'الفريق' : 'Team'}</TableHead>
                  <TableHead className="font-semibold whitespace-nowrap">{isRTL ? 'التنفيذ' : 'Impl.'}</TableHead>
                  <TableHead className="font-semibold whitespace-nowrap">{isRTL ? 'التسليم' : 'Handover'}</TableHead>
                  <TableHead className="font-semibold whitespace-nowrap">{isRTL ? 'الفواتير' : 'Invoices'}</TableHead>
                  <TableHead className="font-semibold whitespace-nowrap">{isRTL ? 'التقدم' : 'Progress'}</TableHead>
                  <TableHead className="font-semibold whitespace-nowrap">{isRTL ? 'الحالة' : 'Status'}</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedSites.map(site => {
                  const wf = site.workflow || {};
                  const poStatus = wf.purchase_order?.status || 'not_started';
                  const materialStatus = wf.material_issue?.status === 'issued' ? 'issued' : (wf.material_receive?.status || 'not_started');
                  const implStatus = wf.implementation?.status || 'not_started';
                  const handoverStatus = wf.handover?.status || 'not_started';
                  const teamCount = wf.team_assigned?.length || 0;
                  const invoiceCount = wf.invoices?.length || 0;
                  const progress = wf.overall_progress || 0;
                  
                  return (
                    <TableRow 
                      key={site.id} 
                      className="hover:bg-muted/30 cursor-pointer"
                      onClick={() => navigate(`/projects/sites/${site.id}`)}
                    >
                      <TableCell className="font-mono font-medium text-primary">{site.site_number}</TableCell>
                      <TableCell className="font-medium max-w-[150px] truncate">{site.name}</TableCell>
                      <TableCell className="text-muted-foreground max-w-[120px] truncate">
                        {site.project_name || '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground max-w-[120px] truncate">
                        {site.client_name || '-'}
                      </TableCell>
                      <TableCell className="font-mono">
                        {wf.purchase_order?.po_number || <span className="text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell>{getWorkflowBadge(poStatus)}</TableCell>
                      <TableCell className="text-right font-mono">
                        {wf.purchase_order?.amount ? `${currencySymbol} {wf.purchase_order.amount.toLocaleString()}` : '-'}
                      </TableCell>
                      <TableCell>{getWorkflowBadge(materialStatus)}</TableCell>
                      <TableCell>
                        {teamCount > 0 ? (
                          <Badge className="bg-purple-100 text-purple-700 text-xs">
                            <Users className="h-3 w-3 mr-1" />{teamCount}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs bg-gray-50 text-gray-500">-</Badge>
                        )}
                      </TableCell>
                      <TableCell>{getWorkflowBadge(implStatus)}</TableCell>
                      <TableCell>{getWorkflowBadge(handoverStatus)}</TableCell>
                      <TableCell>
                        {invoiceCount > 0 ? (
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs">{invoiceCount}</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs bg-gray-50 text-gray-500">-</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 min-w-[80px]">
                          <Progress value={progress} className="h-1.5 w-12" />
                          <span className="text-xs font-medium">{progress}%</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={statusColors[site.status]}>
                          {statusLabels[site.status] || site.status}
                        </Badge>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/projects/sites/${site.id}`)}>
                              <Eye className="h-4 w-4 mr-2" /> {isRTL ? 'عرض' : 'View'}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openEdit(site)}>
                              <Pencil className="h-4 w-4 mr-2" /> {isRTL ? 'تعديل' : 'Edit'}
                            </DropdownMenuItem>
                            {site.project_id && (
                              <DropdownMenuItem onClick={() => navigate(`/projects/${site.project_id}`)}>
                                <Briefcase className="h-4 w-4 mr-2" /> {isRTL ? 'المشروع' : 'Project'}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(site.id)}>
                              <Trash2 className="h-4 w-4 mr-2" /> {isRTL ? 'حذف' : 'Delete'}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {paginatedSites.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={15} className="text-center py-8 text-muted-foreground">
                      <MapPin className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>{isRTL ? 'لا توجد مواقع' : 'No sites found'}</p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
          <span className="text-sm text-muted-foreground">
            {isRTL 
              ? `عرض ${(currentPage - 1) * itemsPerPage + 1} إلى ${Math.min(currentPage * itemsPerPage, filteredSites.length)} من ${filteredSites.length}`
              : `Showing ${(currentPage - 1) * itemsPerPage + 1} to ${Math.min(currentPage * itemsPerPage, filteredSites.length)} of ${filteredSites.length}`
            }
          </span>
          <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm px-2">
              {currentPage} / {totalPages}
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingSite ? t('jobManagement.editSite') : t('jobManagement.newSite')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('jobManagement.projectName')}</Label>
              <Select value={formData.project_id || ''} onValueChange={v => setFormData({...formData, project_id: v})}>
                <SelectTrigger><SelectValue placeholder={isRTL ? "اختر المشروع" : "Select Project"} /></SelectTrigger>
                <SelectContent>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.project_name || p.title} ({p.job_number})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('jobManagement.client')} *</Label>
              <Select value={formData.client_id || ''} onValueChange={v => setFormData({...formData, client_id: v})}>
                <SelectTrigger><SelectValue placeholder={t('jobManagement.selectClient')} /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('jobManagement.siteName')} *</Label>
              <Input value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="Main Office - Floor 5" />
            </div>
            <div className="space-y-2">
              <Label>{t('jobManagement.description')}</Label>
              <Textarea value={formData.description || ''} onChange={e => setFormData({...formData, description: e.target.value})} rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('jobManagement.address')}</Label>
                <Input value={formData.address || ''} onChange={e => setFormData({...formData, address: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>{t('jobManagement.city')}</Label>
                <Input value={formData.city || ''} onChange={e => setFormData({...formData, city: e.target.value})} placeholder="Riyadh" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('jobManagement.contactPerson')}</Label>
                <Input value={formData.contact_person || ''} onChange={e => setFormData({...formData, contact_person: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>{t('jobManagement.contactPhone')}</Label>
                <Input value={formData.contact_phone || ''} onChange={e => setFormData({...formData, contact_phone: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('jobManagement.contactEmail')}</Label>
              <Input type="email" value={formData.contact_email || ''} onChange={e => setFormData({...formData, contact_email: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>{t('pages.common.cancel')}</Button>
            <Button onClick={handleSave}>{editingSite ? t('pages.common.save') : t('pages.common.add')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Sites;
